import { useEffect } from 'react'

/**
 * Global scroll lock for modals.
 *
 * When any modal is open, lock scrolling on `<html>` (the actual scroll
 * container in this app — the layout uses `min-h-screen` on the body, so
 * scroll lives on the document element). `<body>` is also locked as a
 * belt-and-braces guard for any future layout that scrolls inside body.
 *
 * Layout shift is prevented by the global CSS in `index.css`:
 *   html { overflow-y: scroll; scrollbar-gutter: stable; }
 * The scrollbar gutter is reserved at all times, so toggling overflow does
 * NOT jitter fixed/centered elements sideways.
 *
 * A module-level counter handles stacked modals (e.g. `ConfirmDialog` opened
 * on top of a drawer) — the lock is only released when the last modal
 * closes.
 */

let lockCount = 0
let prevHtmlOverflow: string | null = null
let prevBodyOverflow: string | null = null

function applyLock() {
  if (typeof document === 'undefined') return
  if (lockCount === 0) {
    prevHtmlOverflow = document.documentElement.style.overflow
    prevBodyOverflow = document.body.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
  }
  lockCount += 1
}

function releaseLock() {
  if (typeof document === 'undefined') return
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount === 0) {
    document.documentElement.style.overflow = prevHtmlOverflow ?? ''
    document.body.style.overflow = prevBodyOverflow ?? ''
    prevHtmlOverflow = null
    prevBodyOverflow = null
  }
}

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    applyLock()
    return () => releaseLock()
  }, [active])
}
